import * as React from "react";
import {
  createFileRoute,
  Outlet,
  useCanGoBack,
  useLocation,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftIcon,
  ArrowsRightLeftIcon,
  CreditCardIcon,
  PaintBrushIcon,
  PhotoIcon,
  SettingsIcon,
  SparklesIcon,
  TagIcon,
  UserPlusIcon,
} from "@/src/components/icons.tsx";
import { AppHeader } from "@/src/components/AppHeader.tsx";

const SettingsLayout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const router = useRouter();
  const canGoBack = useCanGoBack();

  const isActive = (path: string) => location.pathname.endsWith(path);

  return (
    <>
      <div className="flex flex-col h-full w-full overflow-hidden">
        <AppHeader />
        <div className="flex flex-1 overflow-hidden bg-gray-50">
          <aside className="w-72 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
            <div className="p-4 border-b border-gray-100">
              <button
                onClick={() => {
                  if (canGoBack) {
                    router.history.back();
                  } else {
                    navigate({ to: "/", search: { returnTab: undefined } });
                  }
                }}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-black transition-colors mb-4"
              >
                <ArrowLeftIcon className="w-4 h-4" /> {t("studio.back")}
              </button>
              <h1 className="text-xl tracking-tight font-semibold text-gray-900">
                {t("settings.settingsTitle")}
              </h1>
            </div>
            <nav className="flex-grow p-4 space-y-1">
              <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                {t("settings.accountStatus")}
              </div>
              <button
                onClick={() =>
                  navigate({ to: "/settings/account", replace: true })
                }
                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${isActive("/account") ? "bg-black text-white" : "text-gray-700 hover:bg-gray-100 hover:text-black"}`}
              >
                <CreditCardIcon className="w-5 h-5 shrink-0" />
                <span className="flex-1 min-w-0 truncate">
                  {t("settings.accountStatus")}
                </span>
              </button>
              <div className="px-4 py-2 mt-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                {t("settings.general")}
              </div>
              <button
                onClick={() =>
                  navigate({ to: "/settings/general", replace: true })
                }
                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${isActive("/general") ? "bg-black text-white" : "text-gray-700 hover:bg-gray-100 hover:text-black"}`}
              >
                <SettingsIcon className="w-5 h-5 shrink-0" />
                <span className="flex-1 min-w-0 truncate">
                  {t("settings.configuration")}
                </span>
              </button>
              <button
                onClick={() =>
                  navigate({ to: "/settings/badge", replace: true })
                }
                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${isActive("/badge") ? "bg-black text-white" : "text-gray-700 hover:bg-gray-100 hover:text-black"}`}
              >
                <TagIcon className="w-5 h-5 shrink-0" />
                <span className="flex-1 min-w-0 truncate">
                  {t("settings.aiBadge")}
                </span>
              </button>
              <div className="px-4 py-2 mt-4 flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                {t("settings.integrations")}
                <span className="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-blue-700">
                  {t("settings.integrationsNew")}
                </span>
              </div>
              <button
                onClick={() =>
                  navigate({ to: "/settings/shopvibes", replace: true })
                }
                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${isActive("/shopvibes") ? "bg-black text-white" : "text-gray-700 hover:bg-gray-100 hover:text-black"}`}
              >
                <ArrowsRightLeftIcon className="w-5 h-5 shrink-0" />
                <span className="flex-1 min-w-0 truncate">Shopvibes</span>
              </button>
              <div className="px-4 py-2 mt-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                {t("settings.projectContext")}
              </div>
              <button
                onClick={() =>
                  navigate({ to: "/settings/brand", replace: true })
                }
                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${isActive("/brand") ? "bg-black text-white" : "text-gray-700 hover:bg-gray-100 hover:text-black"}`}
              >
                <SparklesIcon className="w-5 h-5 shrink-0" />
                <span className="flex-1 min-w-0 truncate">
                  {t("settings.brandIdentity")}
                </span>
              </button>
              <button
                onClick={() =>
                  navigate({ to: "/settings/swatches", replace: true })
                }
                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${isActive("/swatches") ? "bg-black text-white" : "text-gray-700 hover:bg-gray-100 hover:text-black"}`}
              >
                <PaintBrushIcon className="w-5 h-5 shrink-0" />
                <span className="flex-1 min-w-0 truncate">
                  {t("settings.savedSwatches")}
                </span>
              </button>
              <button
                onClick={() =>
                  navigate({ to: "/settings/avatars", replace: true })
                }
                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${isActive("/avatars") ? "bg-black text-white" : "text-gray-700 hover:bg-gray-100 hover:text-black"}`}
              >
                <UserPlusIcon className="w-5 h-5 shrink-0" />
                <span className="flex-1 min-w-0 truncate">
                  {t("settings.avatarLibrary")}
                </span>
              </button>
              <button
                onClick={() =>
                  navigate({ to: "/settings/logos", replace: true })
                }
                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${isActive("/logos") ? "bg-black text-white" : "text-gray-700 hover:bg-gray-100 hover:text-black"}`}
              >
                <PhotoIcon className="w-5 h-5 shrink-0" />
                <span className="flex-1 min-w-0 truncate">
                  {t("settings.logoLibrary")}
                </span>
              </button>
            </nav>
          </aside>

          <main className="flex-grow overflow-y-auto p-8">
            <div className="max-w-4xl mx-auto">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export const Route = createFileRoute("/settings")({
  component: SettingsLayout,
});
